/**
 * StandardText Edit Page
 * Edit existing standard text
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Card, CardContent, CircularProgress } from '@mui/material';
import { PageShell } from '../../components';
import { AvisoDeFalhaAoCarregar } from '../../components/AvisoDeFalhaAoCarregar';
import { falhaDeCarga, textoDaFalhaDeCarga, type FalhaDeCarga } from '../../utils/acessoNegado';
import { standardTextService } from '../../services/standardTextService';
import { StandardText, StandardTextFormData } from '../../types/standardText';
import { StandardTextForm } from './components/StandardTextForm';

const BREADCRUMBS = [
  { label: 'Início', path: '/' },
  { label: 'Textos Padronizados', path: '/standard-texts' },
  { label: 'Editar' },
];

const getApiErrorMessage = (err: unknown, fallback: string): string => {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const { response } = err as { response?: { data?: { error?: string } } };
    if (response?.data?.error) return response.data.error;
  }
  return err instanceof Error ? err.message : fallback;
};

export const StandardTextEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [falha, setFalha] = useState<FalhaDeCarga | null>(null);
  // A recusa do salvar mora ao lado do conteúdo, e não no alerta do topo:
  // é lá que está o texto que ela manda corrigir.
  const [erroDeEnvio, setErroDeEnvio] = useState<string | null>(null);
  const [text, setText] = useState<StandardText | null>(null);
  const [categories, setCategories] = useState<Array<{ value: string; label: string }>>([]);

  const loadText = useCallback(async () => {
    try {
      setLoading(true);
      setFalha(null);
      const data = await standardTextService.getStandardTextById(id!);
      setText(data);
    } catch (err: unknown) {
      setFalha(falhaDeCarga(err, 'Não foi possível carregar o texto padronizado. Tente de novo.'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadCategories = useCallback(async () => {
    try {
      const cats = await standardTextService.getCategories();
      setCategories(cats);
    } catch (err: unknown) {
      console.error('Erro ao carregar categorias:', err);
    }
  }, []);

  useEffect(() => {
    if (id) {
      loadText();
      loadCategories();
    }
  }, [id, loadText, loadCategories]);

  const handleSubmit = async (data: Partial<StandardTextFormData>) => {
    try {
      setSubmitting(true);
      setErroDeEnvio(null);
      await standardTextService.updateStandardText(id!, data);
      navigate('/standard-texts');
    } catch (err: unknown) {
      setErroDeEnvio(getApiErrorMessage(err, 'Erro ao atualizar texto padrão'));
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/standard-texts');
  };

  if (loading) {
    return (
      <PageShell breadcrumbs={BREADCRUMBS} title="Editar Texto Padronizado">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (!text) {
    return (
      <PageShell breadcrumbs={BREADCRUMBS} title="Editar Texto Padronizado">
        <AvisoDeFalhaAoCarregar
          mensagem={textoDaFalhaDeCarga(falha, 'Texto padronizado não encontrado.')}
          rotuloDoBotao="Voltar para textos padronizados"
          aoVoltar={() => navigate('/standard-texts')}
        />
      </PageShell>
    );
  }

  return (
    <PageShell breadcrumbs={BREADCRUMBS} title="Editar Texto Padronizado">
      <Card>
        <CardContent>
          <StandardTextForm
            initialData={text ?? undefined}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={submitting}
            submitError={erroDeEnvio}
            categories={categories}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default StandardTextEditPage;
